import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import SurveyForm from '../components/SurveyForm';
import TableFilter from '../components/TableFilter';
import ActionButtons from '../components/ActionButtons';
import DetailsPanel from '../components/DetailsPanel';
import ReportModal from '../components/ReportModal';

interface Survey {
  id: number;
  title: string;
  description: string;
  type: string;
  is_active: boolean;
  responsible?: number;
  responsible_name?: string;
  created_at: string;
}

const SurveysPage: React.FC = () => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [filteredSurveys, setFilteredSurveys] = useState<Survey[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState<Survey | null>(null);

  // Modal de Relatórios
  const [reportModal, setReportModal] = useState<{ isOpen: boolean; rowId?: number; isRowReport: boolean }>({
    isOpen: false,
    isRowReport: false
  });

  // Master-Detail State
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);

  const fetchSurveys = useCallback(async () => {
    try {
      const response = await api.get('surveys/');
      setSurveys(response.data);
      setFilteredSurveys(response.data);
    } catch (error) {
      console.error("Error fetching surveys", error);
    }
  }, []);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  const handleFilter = (filters: { [key: string]: string }) => {
    let result = surveys;

    if (filters.title) {
      result = result.filter(s => s.title.toLowerCase().includes(filters.title.toLowerCase()));
    }
    if (filters.type) {
      result = result.filter(s => s.type === filters.type);
    }
    if (filters.status) {
      const isActive = filters.status === 'active';
      result = result.filter(s => s.is_active === isActive);
    }

    setFilteredSurveys(result);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingSurvey(null);
    fetchSurveys();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSurvey(null);
  };

  const handleEdit = (survey: Survey) => {
    setEditingSurvey(survey);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta pesquisa?')) {
      try {
        await api.delete(`surveys/${id}/`);
        fetchSurveys();
        if (selectedSurvey?.id === id) setSelectedSurvey(null);
      } catch (error) {
        console.error("Error deleting survey", error);
        alert('Erro ao excluir pesquisa.');
      }
    }
  };

  const openGeneralReports = () => setReportModal({ isOpen: true, isRowReport: false });
  const openRowReport = (id: number) => setReportModal({ isOpen: true, rowId: id, isRowReport: true });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1>Pesquisas</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Gerencie pesquisas de satisfação e clima</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={openGeneralReports}>Relatórios de Pesquisas</button>
          {!showForm && (
            <button className="btn" onClick={() => setShowForm(true)}>
              + Nova Pesquisa
            </button>
          )}
        </div>
      </div>

      {showForm ? (
        <div style={{ marginBottom: '30px' }}>
          <SurveyForm
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            surveyToEdit={editingSurvey}
          />
        </div>
      ) : (
        <TableFilter
          filters={[
            { key: 'title', label: 'Título' },
            {
              key: 'type', label: 'Tipo', type: 'select', options: [
                { value: 'Communication', label: 'Comunicação' },
                { value: 'Climate', label: 'Clima Escolar' }
              ]
            },
            {
              key: 'status', label: 'Status', type: 'select', options: [
                { value: 'active', label: 'Ativa' },
                { value: 'inactive', label: 'Encerrada' }
              ]
            }
          ]}
          onFilter={handleFilter}
        />
      )}

      {reportModal.isOpen && (
        <ReportModal 
          isOpen={reportModal.isOpen}
          onClose={() => setReportModal({ ...reportModal, isOpen: false })}
          category="Survey"
          rowId={reportModal.rowId}
          isRowReport={reportModal.isRowReport}
        />
      )}

      <div className="page-container">
        <div className="list-container card" style={{ overflowX: 'auto', padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Título</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Responsável</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredSurveys.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-light)' }}>
                    Nenhuma pesquisa encontrada.
                  </td>
                </tr>
              ) : (
                filteredSurveys.map((survey) => (
                  <tr
                    key={survey.id}
                    onClick={() => setSelectedSurvey(survey)}
                    className={selectedSurvey?.id === survey.id ? 'selected' : ''}
                  >
                    <td style={{ fontWeight: '500' }}>{survey.title}</td>
                    <td>
                      <span className="badge badge-info">
                        {survey.type === 'Communication' ? 'Comunicação' : 'Clima Escolar'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${survey.is_active ? 'badge-success' : 'badge-secondary'}`} style={!survey.is_active ? { background: '#e2e8f0', color: '#64748b' } : {}}>
                        {survey.is_active ? 'Ativa' : 'Encerrada'}
                      </span>
                    </td>
                    <td>{survey.responsible_name || '-'}</td>
                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <ActionButtons
                        onReport={() => survey.id && openRowReport(survey.id)}
                        onEdit={() => handleEdit(survey)}
                        onDelete={() => handleDelete(survey.id)}
                        deletePermission="delete_survey"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {selectedSurvey && (
          <DetailsPanel title="Detalhes da Pesquisa" onClose={() => setSelectedSurvey(null)}>
            <div className="details-row">
              <div className="details-label">Título</div>
              <div className="details-value">{selectedSurvey.title}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Tipo</div>
              <div className="details-value">
                {selectedSurvey.type === 'Communication' ? 'Comunicação' : 'Clima Escolar'}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Responsável</div>
              <div className="details-value">{selectedSurvey.responsible_name || '-'}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Status</div>
              <div className="details-value">
                {selectedSurvey.is_active ? (
                  <span className="badge badge-success">Ativa</span>
                ) : (
                  <span className="badge badge-secondary">Encerrada</span>
                )}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Data de Criação</div>
              <div className="details-value">
                {new Date(selectedSurvey.created_at).toLocaleDateString()}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Descrição</div>
              <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
                {selectedSurvey.description || 'Sem descrição.'}
              </div>
            </div>
          </DetailsPanel>
        )}
      </div>
    </div>
  );
};

export default SurveysPage;
